import { watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

export type QueryParamSyncOptions<T> = {
  // What query parameter to sync with
  param: string
  // How to parse the query parameter value into the desired type
  parse?: (value: string) => T
  // How to serialize the value back into a string for the query parameter
  serialize?: (value: T) => string
  // Callback to initialize the value when the component mounts
  onInitialize: (value: T) => void
  // Function to get the current value from the component state
  getValue: () => T
  /**
   * 'sync' - keep query param in sync with values
   * 'consume' - use param once and remove from URL
   */
  mode?: 'sync' | 'consume'
}

/**
 * A composable to sync a query parameter with a component state.
 * @param options
 * @returns An object with an `updateQuery` method to manually update the query parameter.
 */
export function useQueryParamStore<T>(options: QueryParamSyncOptions<T>) {
  const route = useRoute()
  const router = useRouter()

  const raw = route.query[options.param]
  const parse =
    options.parse ?? (defaultParse as unknown as (value: string) => T)
  const serialize =
    options.serialize ?? (defaultSerialize as unknown as (value: T) => string)
  const mode = options.mode ?? 'sync'

  if (typeof raw === 'string') {
    const parsed = parse(raw)
    options.onInitialize(parsed as T)

    if (mode === 'consume') {
      const { [options.param]: _, ...rest } = route.query
      router.replace({ query: rest })
    }
  }

  const updateQuery = () => {
    const newValue = options.getValue()
    const serialized = serialize(newValue)

    router.replace({
      query: {
        ...route.query,
        [options.param]: serialized || undefined,
      },
    })
  }

  if (mode === 'sync') {
    watch(options.getValue, updateQuery, { deep: true })
  }

  return {
    updateQuery,
  }
}

// Helper functions

function defaultParse(value: string): string[] {
  return value ? value.split(',') : []
}

function defaultSerialize(value: string[]): string {
  return Array.isArray(value) ? value.join(',') : String(value)
}
